/**
 * «Ещё» (More) — phone hub for sections that lose their entry point when the
 * sidebar is replaced by the 5-tab BottomNav (База знаний, Календарь,
 * Оргструктура, Обучение, Настройки, Помощь). Groups mirror the sidebar;
 * `soon` is gated exactly as the sidebar does (see Sidebar.tsx / nav.ts).
 * Reachable on wide screens by URL too, where it just lists the same links
 * the rail already shows.
 */
import { useRouter } from 'expo-router'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../src/i18n'
import { Card, Enter, Icon, Tag } from '../../src/ui'
import { Page } from '../../src/ui/Screen'
import { NAV_MORE_GROUPS, type NavItem } from '../../src/app/nav'

const TERTIARY = '$tertiary'

function MoreRow({ item, last, onPress }: { item: NavItem; last: boolean; onPress: () => void }) {
  const { l } = useApp()
  // "soon" sections aren't built yet — non-interactive, dimmed, «Скоро» tag
  // (same treatment as the sidebar; navigation is also blocked at the layout).
  const soon = !!item.soon
  return (
    <XStack
      {...(soon ? null : { onPress })}
      cursor={soon ? 'default' : 'pointer'}
      animation="quick"
      alignItems="center"
      gap={14}
      paddingVertical={13}
      paddingHorizontal={16}
      {...(soon ? null : { hoverStyle: { backgroundColor: '$backgroundHover' }, pressStyle: { scale: 0.98 } })}
      {...(last ? null : { borderBottomWidth: 1, borderColor: '$borderSecondary' })}
    >
      <Stack
        width={34}
        height={34}
        borderRadius={8}
        backgroundColor="$primaryBg"
        alignItems="center"
        justifyContent="center"
        opacity={soon ? 0.5 : 1}
      >
        <Icon name={item.icon} size={17} color="$primary" />
      </Stack>
      <Text flex={1} fontSize={14} fontWeight="600" color="$color" opacity={soon ? 0.6 : 1}>
        {l('nav.' + item.key)}
      </Text>
      {soon ? <Tag tone="default">{l('c.soon')}</Tag> : <Icon name="chevron-right" size={16} color={TERTIARY} />}
    </XStack>
  )
}

export default function MoreScreen() {
  const { l } = useApp()
  const router = useRouter()

  return (
    <Page title={l('nav.more')} maxWidth={760}>
      {NAV_MORE_GROUPS.map((group, gi) => {
        const items = group.items
        if (items.length === 0) return null
        return (
          <Enter key={group.titleKey ?? `g${gi}`} index={Math.min(gi, 11)}>
            <YStack gap={8}>
              {group.titleKey && (
                <Text fontSize={11} fontWeight="700" letterSpacing={0.8} color={TERTIARY} paddingHorizontal={4}>
                  {l(group.titleKey)}
                </Text>
              )}
              <Card overflow="hidden">
                {items.map((it, i) => (
                  <MoreRow
                    key={it.key}
                    item={it}
                    last={i === items.length - 1}
                    onPress={() => router.push(it.route)}
                  />
                ))}
              </Card>
            </YStack>
          </Enter>
        )
      })}
      <Stack height={8} />
    </Page>
  )
}
